const STATE_TO_ABBR: Record<string, string> = {
  alabama: "AL", alaska: "AK", arizona: "AZ", arkansas: "AR", california: "CA",
  colorado: "CO", connecticut: "CT", delaware: "DE", florida: "FL", georgia: "GA",
  hawaii: "HI", idaho: "ID", illinois: "IL", indiana: "IN", iowa: "IA",
  kansas: "KS", kentucky: "KY", louisiana: "LA", maine: "ME", maryland: "MD",
  massachusetts: "MA", michigan: "MI", minnesota: "MN", mississippi: "MS", missouri: "MO",
  montana: "MT", nebraska: "NE", nevada: "NV", "new hampshire": "NH", "new jersey": "NJ",
  "new mexico": "NM", "new york": "NY", "north carolina": "NC", "north dakota": "ND",
  ohio: "OH", oklahoma: "OK", oregon: "OR", pennsylvania: "PA", "rhode island": "RI",
  "south carolina": "SC", "south dakota": "SD", tennessee: "TN", texas: "TX", utah: "UT",
  vermont: "VT", virginia: "VA", washington: "WA", "west virginia": "WV",
  wisconsin: "WI", wyoming: "WY", "district of columbia": "DC",
  // International states/regions with a well-known short form
  "ciudad de méxico": "CDMX", "ciudad de mexico": "CDMX",
  "distrito federal": "CDMX", cdmx: "CDMX",
  "estado de méxico": "Edomex", "estado de mexico": "Edomex",
}

// Connector words kept lowercase inside place names ("Ciudad de México",
// not "Ciudad De México"). First word always stays capitalized.
const LOWERCASE_CONNECTORS = new Set(["de", "del", "la", "las", "los", "el", "y", "of", "the"])

// Alternate spellings of the same city, mapped to one canonical (lowercase)
// key so filters/grouping don't split one city into several entries.
const CITY_ALIASES: Record<string, string> = {
  "mexico city": "ciudad de méxico",
  "ciudad de mexico": "ciudad de méxico",
  cdmx: "ciudad de méxico",
  // Geocoder-style municipal names a shop owner pasted as their city.
  "city of rochester": "rochester",
}

/**
 * Lowercase canonical key for a city — use for deduping and equality checks,
 * not for display. "Mexico City", "CDMX" and "Ciudad de México" all yield
 * "ciudad de méxico".
 */
export function canonicalCityKey(city: string): string {
  const key = (city || "").trim().toLowerCase()
  return CITY_ALIASES[key] || key
}

/**
 * Formats a city/town name to capitalize each word
 * Example: "berwyn" → "Berwyn", "new york" → "New York"
 */
export function formatCity(city: string): string {
  if (!city || typeof city !== "string") return ""
  // Hand-typed values carry stray spaces ("Chicago ", "New  York"); a split on
  // single spaces turned those into "Chicago , IL" once joined with a state.
  return city
    .trim()
    .split(/\s+/)
    .map((word, i) => {
      const lower = word.toLowerCase()
      if (i > 0 && LOWERCASE_CONNECTORS.has(lower)) return lower
      return word.charAt(0).toUpperCase() + lower.slice(1)
    })
    .join(" ")
}

/**
 * Formats a state to its 2-letter abbreviation (uppercase)
 * Handles both abbreviations ("il" → "IL") and full names ("illinois" → "IL")
 */
export function formatState(state: string): string {
  if (!state || typeof state !== "string") return ""
  const trimmed = state.trim()
  // Try to match a known name/abbreviation first (handles "CDMX" etc.)
  const abbr = STATE_TO_ABBR[trimmed.toLowerCase()]
  if (abbr) return abbr
  // If it's already a 2-letter abbreviation, just uppercase it
  if (trimmed.length <= 2) return trimmed.toUpperCase()
  // Unknown long name (e.g. international state): title-case, don't shout
  return formatStateName(trimmed)
}

/**
 * Formats a US state name to capitalize first letter
 * Example: "illinois" → "Illinois", "california" → "California"
 */
export function formatStateName(stateName: string): string {
  if (!stateName || typeof stateName !== "string") return ""
  return stateName
    .trim()
    .split(/\s+/)
    .map((word, i) => {
      const lower = word.toLowerCase()
      if (i > 0 && LOWERCASE_CONNECTORS.has(lower)) return lower
      return word.charAt(0).toUpperCase() + lower.slice(1)
    })
    .join(" ")
}

/**
 * "City, ST" for display, from whatever was typed: trims and collapses
 * spaces, title-cases the city, abbreviates known states. Either part may be
 * missing ("Chicago", "IL"), unlike formatLocation, which needs both.
 * Use this instead of `[city, state].filter(Boolean).join(", ")`.
 */
export function displayLocation(city?: string | null, state?: string | null): string {
  return [formatCity(city || ""), formatState(state || "")].filter(Boolean).join(", ")
}

/**
 * Combines city and state into a formatted location string
 * Example: formatLocation("berwyn", "IL") → "Berwyn, IL"
 */
export function formatLocation(city: string, state: string): string {
  const formattedCity = formatCity(city)
  const formattedState = formatState(state)
  if (!formattedCity || !formattedState) return ""
  return `${formattedCity}, ${formattedState}`
}

/**
 * Combines city and state name into a formatted location string
 * Example: formatLocationWithStateName("berwyn", "illinois") → "Berwyn, Illinois"
 */
export function formatLocationWithStateName(city: string, stateName: string): string {
  const formattedCity = formatCity(city)
  const formattedStateName = formatStateName(stateName)
  if (!formattedCity || !formattedStateName) return ""
  return `${formattedCity}, ${formattedStateName}`
}

// Reverse lookups, built once, used by search: a shop stored as "IL" still
// has to be findable by someone typing "Illinois", and the other way round.
const ABBR_TO_STATE_NAMES = Object.entries(STATE_TO_ABBR).reduce<Record<string, string[]>>(
  (acc, [name, abbr]) => {
    const key = abbr.toLowerCase()
    acc[key] = acc[key] || []
    acc[key].push(name)
    return acc
  },
  {},
)

const CANONICAL_TO_CITY_ALIASES = Object.entries(CITY_ALIASES).reduce<Record<string, string[]>>(
  (acc, [alias, canonical]) => {
    acc[canonical] = acc[canonical] || []
    acc[canonical].push(alias)
    return acc
  },
  {},
)

/**
 * Lowercase strings a search should treat as "this state". Covers the stored
 * value plus its abbreviation and full name, so "TX", "Texas" and "texas" all
 * find the same shops.
 */
export function stateSearchTerms(state: string): string[] {
  const raw = (state || "").trim().toLowerCase()
  if (!raw) return []
  const terms = new Set<string>([raw])
  const abbr = STATE_TO_ABBR[raw]
  if (abbr) terms.add(abbr.toLowerCase())
  // Stored value is already an abbreviation — add every full name behind it.
  ABBR_TO_STATE_NAMES[raw]?.forEach((name) => terms.add(name))
  return Array.from(terms)
}

/**
 * Lowercase strings a search should treat as "this city" — the stored value,
 * its canonical key, and every alias spelling, so "CDMX" and "Mexico City"
 * both find a shop stored as "Ciudad de México".
 */
export function citySearchTerms(city: string): string[] {
  const raw = (city || "").trim().toLowerCase()
  if (!raw) return []
  const canonical = canonicalCityKey(raw)
  const terms = new Set<string>([raw, canonical])
  CANONICAL_TO_CITY_ALIASES[canonical]?.forEach((alias) => terms.add(alias))
  return Array.from(terms)
}

/**
 * One-line street address for a shop, with the redundant parts dropped.
 *
 * Shops seeded or self-onboarded without a street number often have their
 * city typed into `address` as well, which rendered as "Costa Mesa, Costa
 * Mesa, CA 92626". Anything that is just the city again is dropped, as are
 * empty fields, so a shop missing its street still reads as a real address
 * rather than a line of stray commas.
 */
export function formatShopAddress(shop: {
  address?: string | null
  city?: string | null
  state?: string | null
  zip?: string | null
}): string {
  const city = formatCity((shop.city || "").trim())
  const street = (shop.address || "").trim()
  const region = [formatState((shop.state || "").trim()), (shop.zip || "").trim()]
    .filter(Boolean)
    .join(" ")

  // Same place written twice — keep the city, drop the echo.
  const streetIsCity = street && city && canonicalCityKey(street) === canonicalCityKey(city)

  return [streetIsCity ? "" : street, city, region].filter(Boolean).join(", ")
}
